import React from 'react'
import {
  Anchor,
  Button,
  Checkbox,
  ConfirmDialog,
  Div,
  Flex,
  IconLink,
  Separator,
  Spacer,
  Text,
  Transition,
} from '@sendoutcards/quantum-design-ui'
// @src imports
import {
  useActions,
  useDebounce,
  useEffect,
  useMutations,
  usePersistedUserData,
  useQueries,
  useQueryParams,
  useRedirectUriBuilder,
  useState,
  useVertical,
} from 'src/hooks'

import { FlexRow } from 'src/styled'
import styles from './loginForm.module.scss'
import LoginInput from './components/LoginInput'
import ChangePasswordForm from './components/ChangePasswordForm'
import {
  AccountInput,
  getMarketingContent,
  getPhoneNumberCountries,
} from 'src/graphql'
import { createPhoneNumberInput } from 'src/helpers/createPhoneNumberInput'
import { getFormattedNumber } from 'src/onboarding/components/PhoneNumberVerification'
import Result from 'src/utils/Result'
import AffiliateAddress from 'src/dashboard/components/AffiliateJoin/AffiliateAddress'
import PaymentMethod from 'src/dashboard/components/AffiliateJoin/PaymentMethod'
import { AffiliateFormInfo } from 'src/marketing/plans/components/AffiliateForm/AffiliateForm'

interface Props {
  didForgetLoginInfo: boolean
  setForgotPassword: (value: boolean) => void
  onCreateAccount: () => void
  isAffiliateJoining?: boolean
}

interface LoginFormState {
  username: string
  password: string
  phoneNumber: string
}

type AffiliateJoinSteps = 'loginAccount' | 'affiliateAddress' | 'paymentMethod'

const LoginForm: React.FC<Props> = props => {
  const {
    didForgetLoginInfo,
    setForgotPassword,
    onCreateAccount,
    isAffiliateJoining,
  } = props
  const shouldShowLoginWithPhoneAnchor = true
  const shouldShowRememberMeCheckbox = true
  const actions = useActions()
  const mutations = useMutations()
  const {
    account: persistedAccount,
    controlData,
    phoneVerification,
  } = usePersistedUserData()
  const queryParams = useQueryParams()
  const redirectUriBuilder = useRedirectUriBuilder()
  const vertical = useVertical()
  const loginPhoneNumber = !controlData.isLoggedIn
    ? phoneVerification.loginPhoneNumber
    : undefined
  const [phoneNumberCountries, marketingCopy] = useQueries(
    getPhoneNumberCountries(),
    getMarketingContent(),
  )
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [loginErrorMessage, setLoginErrorMessage] = useState<
    string | undefined
  >()
  const [values, setValues] = useState<LoginFormState>({
    username: '',
    password: '',
    phoneNumber: loginPhoneNumber?.nationalNumber ?? '',
  })
  const [affiliateStep, setAffiliateStep] = useState<AffiliateJoinSteps>(
    isAffiliateJoining && persistedAccount
      ? 'affiliateAddress'
      : 'loginAccount',
  )
  const { username, password, phoneNumber } = values
  const debouncedPhoneNumber = useDebounce(values.phoneNumber, 1000)
  const [selectedCountryIndex, setSelectedCountryIndex] = useState<number>(
    phoneNumberCountries.findIndex(country => {
      if (loginPhoneNumber) {
        return country.isoCode === loginPhoneNumber.countryIsoCode
      }
      return country.isoCode === 'US'
    }),
  )
  const [isLoading, setIsLoading] = useState(false)
  const [isRememberMeChecked, setIsRememberMeChecked] = useState(true)
  const [shouldLoginWithPhone, setShouldLoginWithPhone] = useState(
    !!loginPhoneNumber,
  )
  const [formattedNumber, setFormattedNumber] = useState<string>('')
  const [loginWithPhonenumberError, setLoginWithPhonenumberError] = useState<
    string
  >()
  const [isPhoneNumberChanging, setIsPhoneNumberChanging] = useState(false)
  const [shouldShowUnverifiedDialog, setShouldShowUnverifiedDialog] = useState(
    phoneVerification.unverifiedAccountsFound ?? false,
  )
  const [
    shouldShowPhoneNotFoundDialog,
    setShouldShowPhoneNotFoundDialog,
  ] = useState(phoneVerification.noPhoneFound ?? false)
  const phoneNumberInput = createPhoneNumberInput(
    debouncedPhoneNumber,
    phoneNumberCountries[selectedCountryIndex],
  )
  const [affiliateFormInfo, setAffiliateFormInfo] = useState<AffiliateFormInfo>(
    {
      user: {
        firstName: '',
        lastName: '',
        company: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        country: '',
        postalCode: '',
      },
      govId1: '',
      govId2: '',
      phoneNumber: '',
      accepted: false,
      purchased: false,
    },
  )
  const [failureMessage, setFailureMessage] = useState<string>()

  const [details, setDetails] = useState({
    email: persistedAccount?.email ?? '',
    username: persistedAccount?.username ?? '',
    password: '',
  })

  useEffect(() => {
    if (debouncedPhoneNumber !== phoneNumber) {
      setIsPhoneNumberChanging(true)
      setLoginWithPhonenumberError(undefined)
    } else {
      setIsPhoneNumberChanging(false)
    }
  }, [
    debouncedPhoneNumber,
    phoneNumber,
    setIsPhoneNumberChanging,
    setLoginWithPhonenumberError,
  ])

  const handleChange = (name: keyof LoginFormState, value: string) => {
    setValues({ ...values, [name]: value })
  }
  const loginEvent: React.ReactEventHandler<HTMLFormElement> = event => {
    event.preventDefault()
    loginUser()
  }

  const loginUser = async () => {
    setIsLoggingIn(true)
    setLoginErrorMessage(undefined)
    const {
      loginUser: { account, message },
      createUserLoginLink: { link },
    } = await mutations.loginUser({
      username,
      password,
      verticalId: vertical.graphqlVerticalID,
      queryParams,
    })

    if (account) {
      if (details) {
        setDetails(x => ({ ...x, password: '' }))
      }
      if (isAffiliateJoining) {
        actions.loadedUser(Result(account))
        setAffiliateStep('affiliateAddress')
      } else {
        actions.loginDone(Result(account), isRememberMeChecked)
        const redirectUri = link || redirectUriBuilder(account)
        if (redirectUri) {
          window.location.href = redirectUri
        } else {
          actions.openAccount()
        }
      }
    } else {
      setLoginErrorMessage(
        message === 'Invalid Credentials'
          ? 'Wrong username or password'
          : message
          ? message
          : 'Unable to login.',
      )
    }
    setIsLoggingIn(false)
  }

  const errorMessage =
    !didForgetLoginInfo && !shouldLoginWithPhone && loginErrorMessage
      ? loginErrorMessage
      : !isPhoneNumberChanging &&
        debouncedPhoneNumber !== '' &&
        !phoneNumberInput.nationalNumber
      ? 'Please enter a valid phone number'
      : loginWithPhonenumberError ?? ''

  const handleLoginWithPhonenumber = async () => {
    try {
      setFormattedNumber(
        getFormattedNumber(
          phoneNumberInput.countryIsoCode,
          phoneNumberInput.nationalNumber,
        ),
      )
      setIsLoading(true)
      const {
        requestPhoneNumberVerification: result,
      } = await mutations.requestPhoneNumberVerification({
        phoneNumber: phoneNumberInput,
        doNotShowDefaultTransition: true,
      })
      if (result.__typename === 'RequestPhoneNumberVerificationSuccess') {
        actions.openPhoneNumberVerification(
          phoneNumberInput,
          undefined,
          true,
          false,
          isRememberMeChecked,
        )
        setLoginWithPhonenumberError(undefined)
      } else {
        setLoginWithPhonenumberError(
          `Failed to send code to: ${formattedNumber}`,
        )
      }
    } catch (error) {
      setLoginWithPhonenumberError(error.toString())
    } finally {
      setIsLoading(false)
    }
  }

  const hasAllRequiredFields = (info: AffiliateFormInfo): boolean => {
    return (
      info.user.firstName !== '' &&
      info.user.lastName !== '' &&
      info.user.address1 !== '' &&
      info.user.postalCode !== '' &&
      info.user.city !== '' &&
      info.user.state !== '' &&
      info.user.country !== '' &&
      info.govId1 !== '' &&
      info.accepted &&
      (!!info.stripeToken || !!persistedAccount?.stripeSource)
    )
  }

  const onSubmitAffiliateJoin = async (info: AffiliateFormInfo) => {
    setAffiliateFormInfo(info)

    switch (affiliateStep) {
      case 'affiliateAddress':
        setAffiliateStep('paymentMethod')
        return
      case 'paymentMethod':
        if (!hasAllRequiredFields(info)) {
          return
        }

        const accountInput: AccountInput = {
          firstName: info.user.firstName,
          lastName: info.user.lastName,
          shippingAddress: { ...info.user },
        }

        try {
          const { govId1, govId2, stripeToken } = info
          const {
            createDistributor: { account, error },
            createUserLoginLink: { link },
          } = await mutations.createAffiliate({
            account: {
              ...accountInput,
            },
            govId1,
            govId2,
            stripeToken: stripeToken?.id,
            verticalId: vertical.graphqlVerticalID,
            queryParams,
          })
          if (account) {
            actions.loginDone(Result(account), isRememberMeChecked)
            const redirectUri = link || redirectUriBuilder(account)
            if (redirectUri) {
              window.location.href = redirectUri
            } else {
              actions.openAccount()
            }
          } else {
            setFailureMessage(error ?? 'Failed to create affiliate account.')
          }
        } catch (error) {
          setFailureMessage(
            error?.message ?? 'Failed to create affiliate account.',
          )
        }
        return
      default:
        break
    }
  }

  if (isLoading) {
    return (
      <Transition
        isLoading={isLoading}
        title="Sending Code To:"
        subtitle={formattedNumber}
      />
    )
  }

  const isJoiningAffiliate = queryParams.isJoiningAffiliate

  return didForgetLoginInfo ? (
    <ChangePasswordForm
      toggleForgotPassword={() => setForgotPassword(!didForgetLoginInfo)}
    />
  ) : (
    <>
      {affiliateStep === 'loginAccount' && (
        <form className={styles.userForm} onSubmit={loginEvent}>
          {vertical.id === 'soc' && (
            <Text type="title" weight="semiBold">
              Welcome to SendOutCards
            </Text>
          )}
          {queryParams.isJoiningAffiliate && (
            <Text type="subtitle">Become an Affiliate</Text>
          )}
          <Spacer space="x2" />
          <Text type="caption" alignment="center">
            {isJoiningAffiliate
              ? marketingCopy.affiliateLoginMessage.content
              : marketingCopy.loginWelcomeMessage.content}
          </Text>
          <Spacer space="x1" />
          <LoginInput
            selectedCountryIndex={selectedCountryIndex}
            setSelectedCountryIndex={setSelectedCountryIndex}
            phoneNumberCountries={phoneNumberCountries}
            shouldLoginWithPhone={shouldLoginWithPhone}
            onChange={handleChange}
            username={username}
            password={password}
            phoneNumber={phoneNumber}
            errorMessage={errorMessage}
          />
          <Flex
            outset={{ top: shouldLoginWithPhone ? '-8px' : 'x2' }}
            width="100%"
            justifyContent="space-between"
          >
            <div style={{ marginLeft: '-13px' }}>
              {shouldShowRememberMeCheckbox && (
                <Checkbox
                  onChange={setIsRememberMeChecked}
                  isChecked={isRememberMeChecked}
                  label="Remember Me"
                  type="primary"
                />
              )}
            </div>
            <div style={{ marginLeft: '5px' }}>
              <Anchor
                size="xSmall"
                title="Forgot password?"
                color="anchorBlue"
                isDecorated={true}
                onClick={() => {
                  setValues({ ...values })
                  setForgotPassword(!didForgetLoginInfo)
                }}
              />
            </div>
          </Flex>
          <Spacer space="x4" />
          <Div id="login_btn" width="100%">
            <Button
              hover={true}
              onClick={
                shouldLoginWithPhone
                  ? () => handleLoginWithPhonenumber()
                  : 'submitForm'
              }
              type={'primary'}
              fullWidth={true}
              title={!isLoggingIn ? 'Log In' : 'Logging in...'}
              disabled={
                shouldLoginWithPhone
                  ? phoneNumberInput.nationalNumber === undefined
                  : !username || !password || isLoggingIn
              }
            />
          </Div>
          <Spacer space="x1" />
          <Div style={{ width: '100%' }}>
            <FlexRow>
              <Div style={{ cursor: 'pointer' }}>
                {shouldShowLoginWithPhoneAnchor && (
                  <IconLink
                    size="xSmall"
                    title={`Login with ${
                      shouldLoginWithPhone ? 'Username' : 'Phone Number'
                    }`}
                    icon="information"
                    onClick={() =>
                      setShouldLoginWithPhone(!shouldLoginWithPhone)
                    }
                  />
                )}
              </Div>
              <Div />
            </FlexRow>
          </Div>
          <Spacer space="x4" />
          <Separator orientation="horizontal" dashed={true} caption="Or" />
          <Spacer space="x4" />
          {vertical.id === 'soc' && (
            <>
              <Div width="100%">
                <Button
                  hover={true}
                  fullWidth={true}
                  title="Send Your First Card Free"
                  onClick={() => {
                    actions.openPhoneNumberVerification()
                  }}
                  type="secondary"
                />
              </Div>
              <Spacer space="x2" />
            </>
          )}
          <Button
            hover={true}
            fullWidth={true}
            title="Create an Account"
            outlined={true}
            onClick={() => onCreateAccount()}
            type="secondary"
          />
          <ConfirmDialog
            isOpen={shouldShowUnverifiedDialog}
            title={marketingCopy.unverifiedAccountsFoundCopy.title}
            description={marketingCopy.unverifiedAccountsFoundCopy.content}
            accept={{
              title: 'Got it',
              onClick: () => setShouldShowUnverifiedDialog(false),
            }}
            primaryAction={'accept'}
            onClose={() => setShouldShowUnverifiedDialog(false)}
          />
          <ConfirmDialog
            isOpen={shouldShowPhoneNotFoundDialog}
            title={marketingCopy.phoneNotFoundCopy.title}
            description={marketingCopy.phoneNotFoundCopy.content}
            accept={{
              title: 'Got it',
              onClick: () => setShouldShowPhoneNotFoundDialog(false),
            }}
            primaryAction={'accept'}
            onClose={() => setShouldShowPhoneNotFoundDialog(false)}
          />
        </form>
      )}
      {affiliateStep === 'affiliateAddress' && (
        <AffiliateAddress
          affiliateInfo={affiliateFormInfo}
          onFinishAccount={affiliateInfo => {
            onSubmitAffiliateJoin(affiliateInfo)
          }}
          onBack={() => {
            setAffiliateStep('loginAccount')
            actions.unloadUser()
          }}
        />
      )}
      {affiliateStep === 'paymentMethod' && (
        <PaymentMethod
          affiliateInfo={affiliateFormInfo}
          onFinishAccount={affiliateInfo => {
            onSubmitAffiliateJoin(affiliateInfo)
          }}
          onBack={info => {
            setAffiliateFormInfo(info)
            setAffiliateStep('affiliateAddress')
          }}
        />
      )}
      <ConfirmDialog
        zIndex={1}
        isOpen={!!failureMessage}
        title="Failed to Join"
        description={failureMessage ?? ''}
        accept={{
          title: 'OK',
          onClick: () => setFailureMessage(undefined),
        }}
      />
    </>
  )
}

export default LoginForm
